/**
 * Keyboard-wedge burst assembly, kept pure so it is testable without a DOM.
 *
 * A wedge RFID reader is a keyboard: it types the card UID as a burst of
 * keystrokes in a few milliseconds, usually finishing with Enter. Telling that
 * burst apart from a human at the keyboard rests on two observations:
 *
 *  - humans cannot type a whole UID inside {@link IDLE_COMMIT_MS}, so any gap
 *    longer than that starts a fresh buffer — stray typing never accumulates
 *    into something UID-shaped;
 *  - no real UID is shorter than {@link MIN_CARD_ID_LENGTH}, so a lone Enter
 *    or a couple of stray characters never commit.
 *
 * The timing model mirrors the portal's reader hook
 * (apps/portal-web/src/features/auth/useCardReader.ts) — the two apps listen
 * to the same physical readers, so their constants must not drift apart.
 * Portal code is not imported across apps; this is the staff-web copy.
 */

/** Shorter than any real card id; filters stray keypresses. */
export const MIN_CARD_ID_LENGTH = 4;
/** A gap this long means a new burst; readers without Enter commit after it. */
export const IDLE_COMMIT_MS = 400;
/** How long a repeating reader may go quiet before presence is considered lost. */
export const PRESENCE_TIMEOUT_MS = 1_200;

export interface WedgeBuffer {
  /**
   * Feed one keydown. Returns a complete card id when this key finished a
   * burst (an Enter after enough characters), otherwise null.
   */
  feed(key: string, now: number): string | null;
  /**
   * Commit a terminator-less burst once it has gone quiet. Call from a timer:
   * some readers type the UID and never send Enter.
   */
  flushIfIdle(now: number): string | null;
  reset(): void;
}

export function createWedgeBuffer(
  minLength: number = MIN_CARD_ID_LENGTH,
  idleCommitMs: number = IDLE_COMMIT_MS,
): WedgeBuffer {
  let buffer = '';
  let lastKeyAt = 0;

  return {
    feed(key, now) {
      // A gap means a new burst; a reader emits its whole id in milliseconds.
      if (now - lastKeyAt > idleCommitMs) buffer = '';
      lastKeyAt = now;

      if (key === 'Enter') {
        const value = buffer;
        buffer = '';
        return value.length >= minLength ? value : null;
      }

      // Only printable characters; 'Shift', 'F5' etc. have multi-char names.
      if (key.length === 1) buffer += key;
      return null;
    },

    flushIfIdle(now) {
      if (buffer.length >= minLength && now - lastKeyAt > idleCommitMs) {
        const value = buffer;
        buffer = '';
        return value;
      }
      return null;
    },

    reset() {
      buffer = '';
    },
  };
}

/**
 * True for elements whose keystrokes belong to the person typing, not to the
 * reader. While staff fill the assign form, the window listener must not
 * treat their typing as a card.
 */
export function isTextEntryElement(target: {
  tagName: string;
  isContentEditable?: boolean;
}): boolean {
  return (
    target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable === true
  );
}
